import DiffMatchPatch from 'diff-match-patch';
import { ApiError, isApiError, requestJson, type ApiEnvelope } from '@shared/api/http';
import type { RegistrationPolicy } from '@domain/auth/types';
import type { AppConfig, AppConfigPatchPayload } from './types';

const configRecordKey = 'locs:webapp:app-config';
const legacyConfigVersionKey = 'locs:webapp:app-config:version';
const legacyConfigContentKey = 'locs:webapp:app-config:content';
const defaultRequestTimeoutMs = 10_000;
const dmp = new DiffMatchPatch();

type ConfigCacheRecord = {
  version: number;
  content: string;
};

type LoadAppConfigOptions = {
  signal?: AbortSignal;
  requestTimeoutMs?: number;
};

function ensureSuccess<T>(body: ApiEnvelope<T>, fallbackMessage: string): ApiEnvelope<T> {
  if (body.status !== 'Success') {
    throw new ApiError(body.message ?? fallbackMessage, body.statusCode ?? 500, body);
  }
  return body;
}

function readCache(): ConfigCacheRecord {
  if (typeof window === 'undefined') return { version: 0, content: '' };
  try {
    const record = window.localStorage.getItem(configRecordKey);
    if (record) {
      const parsed = JSON.parse(record) as Partial<ConfigCacheRecord>;
      if (typeof parsed.version === 'number' && typeof parsed.content === 'string') {
        return { version: parsed.version, content: parsed.content };
      }
    }
    const legacyVersion = window.localStorage.getItem(legacyConfigVersionKey);
    const legacyContent = window.localStorage.getItem(legacyConfigContentKey) ?? '';
    return {
      version: legacyVersion ? Number.parseInt(legacyVersion, 10) || 0 : 0,
      content: legacyContent,
    };
  } catch {
    return { version: 0, content: '' };
  }
}

function writeCache(version: number, content: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(configRecordKey, JSON.stringify({ version, content }));
  } catch {
    // Remote success remains usable in memory even when persistence is unavailable.
  }
}

async function fetchPatch(
  version: number,
  baseUrl: string | undefined,
  signal: AbortSignal,
): Promise<AppConfigPatchPayload> {
  const body = await requestJson<ApiEnvelope<AppConfigPatchPayload>>(
    `/content/config/patch?version=${encodeURIComponent(String(version))}`,
    { baseUrl, signal, cache: 'no-store' },
  );
  return ensureSuccess(body, 'Failed to load app config').data ?? {
    version: null,
    conf: null,
    patch: null,
  };
}

async function fetchPatchWithTimeout(
  version: number,
  baseUrl: string | undefined,
  parentSignal: AbortSignal | undefined,
  timeoutMs: number,
) {
  const controller = new AbortController();
  const abortFromParent = () => controller.abort(parentSignal?.reason);
  if (parentSignal?.aborted) {
    abortFromParent();
  } else {
    parentSignal?.addEventListener('abort', abortFromParent, { once: true });
  }
  const timeout = globalThis.setTimeout(() => {
    controller.abort(new DOMException('App config request timed out', 'TimeoutError'));
  }, timeoutMs);
  try {
    return await fetchPatch(version, baseUrl, controller.signal);
  } finally {
    globalThis.clearTimeout(timeout);
    parentSignal?.removeEventListener('abort', abortFromParent);
  }
}

function parseConfig(content: string): AppConfig {
  return JSON.parse(content) as AppConfig;
}

function applyPatch(cachedContent: string, patchText: string, expectedOldLength?: number | null) {
  if (typeof expectedOldLength === 'number' && cachedContent.length !== expectedOldLength) {
    throw new Error('Illegal local app config length');
  }
  const patches = dmp.patch_fromText(patchText);
  const [nextContent, results] = dmp.patch_apply(patches, cachedContent) as [string, boolean[]];
  if (results.some((result) => !result)) {
    throw new Error('Illegal app config patch result');
  }
  return nextContent;
}

export async function loadAppConfig(
  baseUrl?: string,
  options: LoadAppConfigOptions = {},
): Promise<AppConfig> {
  const cache = readCache();
  const requestTimeoutMs = options.requestTimeoutMs ?? defaultRequestTimeoutMs;
  let payload: AppConfigPatchPayload;
  try {
    payload = await fetchPatchWithTimeout(
      cache.version,
      baseUrl,
      options.signal,
      requestTimeoutMs,
    );
  } catch (error) {
    const staleVersionRejected = cache.version !== 0
      && isApiError(error)
      && error.status === -1;
    if (!staleVersionRejected) throw error;
    payload = await fetchPatchWithTimeout(0, baseUrl, options.signal, requestTimeoutMs);
  }

  if (payload.conf && typeof payload.version === 'number') {
    try {
      const config = parseConfig(payload.conf);
      writeCache(payload.version, payload.conf);
      return config;
    } catch {
      // A successful but locally invalid delta response receives one full fetch.
    }
  }

  if (payload.patch && typeof payload.version === 'number') {
    try {
      const nextContent = applyPatch(cache.content, payload.patch, payload.olen);
      const config = parseConfig(nextContent);
      writeCache(payload.version, nextContent);
      return config;
    } catch {
      // A remotely delivered but locally invalid patch receives one full-fetch fallback.
    }
  }

  const fullPayload = await fetchPatchWithTimeout(0, baseUrl, options.signal, requestTimeoutMs);
  if (!fullPayload.conf || typeof fullPayload.version !== 'number') {
    throw new ApiError('App config payload is missing', 500, fullPayload);
  }
  const config = parseConfig(fullPayload.conf);
  writeCache(fullPayload.version, fullPayload.conf);
  return config;
}

export async function loadRegistrationPolicy(baseUrl?: string): Promise<RegistrationPolicy> {
  const config = await loadAppConfig(baseUrl);
  if (!config.registrationPolicy) {
    throw new ApiError('Registration policy is missing from app config', 500, config);
  }
  return config.registrationPolicy;
}
